<template>
    <v-container>
        <v-row>
            <v-col cols="4" offset="8">
                <v-text-field
                    ref="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    :loading="loadingSessions"
                    single-line
                    hide-details
                    @input="
                        search => {
                            loadingSessions = true;
                            updateSearch(search);
                        }
                    "
                ></v-text-field>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-expansion-panels v-model="panels" focusable multiple>
                    <v-expansion-panel v-for="(session, index) in sessions" :key="session.id">
                        <v-expansion-panel-header>
                            <v-row no-gutters>
                                <v-col cols="8">
                                    <span class="font-weight-bold">{{ session.title }}</span>
                                    <change-times
                                        :model="session"
                                        :previous="get(sessions, index - 1)"
                                        :model-class="modelClass"
                                        :meet="meet"
                                        :timezone="meet.timezone"
                                        @changed="refresh"
                                    />

                                    <span v-if="session.starts_at">
                                        <datetime-display
                                            :datetime="session.starts_at"
                                            format="ddd h:mm -"
                                            :timezone="meet.timezone"
                                            class="grey--text text--darken-2 font-weight-bold"
                                        />
                                        <datetime-display
                                            v-if="session.ends_at"
                                            :datetime="session.ends_at"
                                            format="h:mm a"
                                            :timezone="meet.timezone"
                                            class="grey--text text--darken-2 font-weight-bold"
                                        />
                                    </span>
                                </v-col>
                                <v-col cols="4" class="text-right">
                                    <v-chip
                                        v-if="session.has_trampoline"
                                        class="v-expansion-panel-header__icon mr-2 blue--text text--darken-3 font-weight-bold"
                                        color="blue lighten-4"
                                        small
                                    >
                                        {{ session.trampoline_flights.length }}
                                    </v-chip>
                                    <v-chip
                                        v-if="session.has_double_mini"
                                        class="v-expansion-panel-header__icon mr-2 green--text text--darken-3 font-weight-bold"
                                        color="green lighten-4"
                                        small
                                    >
                                        {{ session.double_mini_flights.length }}
                                    </v-chip>
                                    <v-chip
                                        v-if="session.has_tumbling"
                                        class="v-expansion-panel-header__icon mr-2 red--text text--darken-3 font-weight-bold"
                                        color="red lighten-4"
                                        small
                                    >
                                        {{ session.tumbling_flights.length }}
                                    </v-chip>
                                    <v-chip
                                        v-if="session.has_synchro"
                                        class="v-expansion-panel-header__icon mr-2 yellow--text text--darken-4 font-weight-bold"
                                        color="yellow lighten-4"
                                        small
                                    >
                                        {{ session.synchro_flights.length }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <flight-start-list
                                v-if="session.trampoline_flights.length > 0"
                                :flights="session.trampoline_flights"
                                :meet="meet"
                                :searching="sharedState.searchQuery !== ''"
                                event="TI"
                            >
                                Trampoline
                            </flight-start-list>

                            <flight-start-list
                                v-if="session.double_mini_flights.length > 0"
                                :flights="session.double_mini_flights"
                                :meet="meet"
                                :searching="sharedState.searchQuery !== ''"
                                event="DM"
                            >
                                Double Mini
                            </flight-start-list>

                            <flight-start-list
                                v-if="session.tumbling_flights.length > 0"
                                :flights="session.tumbling_flights"
                                :meet="meet"
                                :searching="sharedState.searchQuery !== ''"
                                event="TU"
                            >
                                Tumbling
                            </flight-start-list>

                            <flight-start-list
                                v-if="session.synchro_flights.length > 0"
                                :flights="session.synchro_flights"
                                :meet="meet"
                                :searching="sharedState.searchQuery !== ''"
                                event="TS"
                            >
                                Synchro
                            </flight-start-list>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import debounce from 'v-debounce';
import get from 'lodash/get.js';
import FlightStartList from '@/Components/FlightStartList.vue';
import Session from '@/Models/Session.js';
import DatetimeDisplay from '@/Components/DatetimeDisplay.vue';
import Meet from '@/Models/Meet.js';
import ChangeTimes from '@/Components/ChangeTimes.vue';

export default {
    name: 'SessionStartList',

    components: { ChangeTimes, DatetimeDisplay, FlightStartList },

    directives: { debounce },

    provide() {
        return {
            sharedState: this.sharedState,
        };
    },

    props: {
        meetId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        sharedState: {
            searchQuery: '',
        },

        loadingSessions: false,
        panels: [],
        modelClass: Session,
        sessionIds: [],
    }),

    computed: {
        meet() {
            return Meet.find(this.meetId);
        },

        sessions() {
            return Session.query()
                .with([
                    'flights',
                    'flights.start_lists.athlete',
                    'flights.start_lists.athlete2',
                    'flights.start_lists.club',
                    'flights.start_lists.club2',
                ])
                .with('flights.start_lists', query => query.orderBy('order'))
                .whereIdIn(this.sessionIds)
                .get();
        },
    },

    watch: {
        'sharedState.searchQuery': {
            immediate: true,
            async handler(search) {
                await this.refresh();

                if (search) {
                    this.$nextTick(() => this.expandAll());
                }
            },
        },
    },

    created() {
        this.updateSearch = _.debounce(search => {
            this.sharedState.searchQuery = search;

            this.loadingSessions = false;
        }, 500);
    },

    methods: {
        get,

        expandAll() {
            this.panels = [...this.sessions.keys()].map((k, i) => i);
        },

        collapseAll() {
            this.panels = [];
        },

        async refresh() {
            const sessions = await Session.searchMeet(this.meetId, this.sharedState.searchQuery.trim().toLowerCase());

            this.sessionIds = sessions.map(s => s.id);
        },
    },
};
</script>
